import { Component, Input, ViewChild, ViewContainerRef, OnChanges, AfterViewInit, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Variants } from './variants';
import { LoggerService } from '../../services/sp-logger/logger.service';

export const IconSize = {
  s: 12, // => 1
  m: 16, // => 20
  l: 24, // => 28
  xl: 32, // => 36
} as const;

export type IconSize = keyof typeof IconSize;

@Component({
  selector: 'app-titan-icon',
  templateUrl: './titan-icon.component.html',
  styleUrls: ['./titan-icon.component.scss'],
  standalone: true,
  imports: [CommonModule],
  host: {
    '[class]': 'containerClassName',
    '[style.width.px]': 'getIconSize(size) + safeArea',
    '[style.height.px]': 'getIconSize(size) + safeArea',
    '[style.padding]': '"0 " + extraSafeArea',
    '[style]': 'displayContents ? "display: contents" : ""'
  }
})
export class TitanIconComponent implements OnChanges, AfterViewInit {
  @Input() size: IconSize = 'm';
  @Input() variant: keyof typeof Variants = 'Unisex';
  @Input() badgeCount?: number;
  @Input('aria-label') ariaLabel?: string;
  @Input() displayContents: boolean = false;

  @ViewChild('variantContainer', { read: ViewContainerRef }) variantContainer!: ViewContainerRef;
  @ViewChild('svgElement', { read: ElementRef }) svgElement!: ElementRef<SVGElement>;

  private viewInitialized = false;

  constructor(private logger: LoggerService) {
    
  }

  get safeArea(): number {
    return 4;
  }

  get extraSafeArea(): string {
    return '0';
  }

  getIconSize(size: IconSize): number {
    return IconSize[size];
  }

  getViewBox(): string {
    const totalSize = this.getIconSize(this.size) + this.safeArea;
    return `0 0 ${totalSize} ${totalSize}`;
  }

  getContainerStyle(): { [key: string]: string } {
    const totalSize = this.getIconSize(this.size) + this.safeArea;
    return {
      width: `${totalSize}px`,
      height: `${totalSize}px`,
      padding: `0 ${this.extraSafeArea}`
    };
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    this.loadVariantComponent();
  }

  ngOnChanges() {
    if (this.viewInitialized) {
      this.loadVariantComponent();
    }
  }

  private loadVariantComponent() {
    // Clear any existing dynamically added paths
    if (this.svgElement?.nativeElement) {
      const existingPaths = this.svgElement.nativeElement.querySelectorAll('path[data-dynamic="true"]');
      existingPaths.forEach(path => path.remove());
    }

    try {
      // Get variant class directly from imported Variants
      const componentClass = Variants[this.variant];

      if (componentClass) {
        // Create path element directly without Angular component wrapper
        const pathElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');

        // Get path data and attributes from the component class
        const instance = new componentClass();
        instance.size = this.size;
        const pathData = instance.paths[this.size];
        const attributes = instance.attributes;

        // Set path attributes from variant
        pathElement.setAttribute('fill-rule', attributes.fillRule);
        pathElement.setAttribute('clip-rule', attributes.clipRule);
        pathElement.setAttribute('d', pathData);
        pathElement.setAttribute('fill', attributes.fill);
        pathElement.setAttribute('data-dynamic', 'true'); // Mark as dynamically added

        // Append path to the SVG element
        this.svgElement.nativeElement.appendChild(pathElement);
      }
    } catch (error) {
      // Fallback to function-based variant will be handled in template
      this.logger.logError(`Could not load component for variant: ${this.variant}`, error);
    }
  }

  get containerClassName(): string {
    return this.badgeCount !== undefined ? 'titan-icon with-badge' : 'titan-icon';
  }
}
